import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ROUTES } from "../common/constants";
import { PAGE_TITLE } from "../pages-meta";
import { type Revision, listRevisions, revertTo } from "../services/Api";
import { Loader } from "../ui/Loader";

const KIND_LABEL: Record<string, string> = {
  draft_save: "Draft saved",
  publish: "Published",
  revert: "Reverted",
};

function fmt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function errMessage(e: unknown): string {
  return (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Something went wrong.";
}

// "hero.headingHighlight" -> "Hero · Heading highlight"; "ctas.0.label" -> "Ctas · #1 · Label"
function prettyPath(path: string): string {
  return path
    .split(".")
    .map((seg) =>
      /^\d+$/.test(seg)
        ? `#${Number(seg) + 1}`
        : seg
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (c) => c.toUpperCase())
            .trim(),
    )
    .join(" · ");
}

function shown(v: string | null): string {
  if (v === null || v === "") return "(empty)";
  return v.length > 160 ? `${v.slice(0, 160)}…` : v;
}

export function PageHistory() {
  const { slug = "" } = useParams();
  const [revisions, setRevisions] = useState<Revision[] | null>(null);

  function load() {
    listRevisions(slug)
      .then((r) => setRevisions(r.data))
      .catch((e) => toast.error(errMessage(e)));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function doRevert(id: number) {
    if (!window.confirm("Revert the draft to this revision? You can review and publish it afterwards.")) return;
    try {
      await revertTo(slug, id);
      toast.success("Draft reverted — open the editor to review and publish.");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  }

  return (
    <div className="page">
      <div className="page-title-row">
        <div>
          <Link to={ROUTES.PAGE_EDITOR(slug)} className="back-link">
            ← Editor
          </Link>
          <h1>History — {PAGE_TITLE[slug] ?? slug}</h1>
        </div>
      </div>

      {revisions === null && <Loader label="Loading history…" />}
      {revisions && revisions.length === 0 && <p className="muted">No history yet.</p>}

      <div className="history-list">
        {revisions?.map((r, idx) => {
          const isOldest = idx === revisions.length - 1;
          return (
            <div className="history-item" key={r.id}>
              <div className="history-meta">
                <span className={`badge ${r.kind === "publish" ? "badge-published" : "badge-draft"}`}>
                  {KIND_LABEL[r.kind] ?? r.kind}
                </span>
                <span className="history-who">{r.actor ?? "Unknown"}</span>
                <span className="history-when">{fmt(r.created_at)}</span>
                <button className="btn-ghost history-revert" onClick={() => doRevert(r.id)}>
                  Revert
                </button>
              </div>

              <div className="history-changes">
                {r.changes.length === 0 ? (
                  <span className="muted">
                    {isOldest
                      ? "Initial version"
                      : r.kind === "publish"
                        ? "Published the current draft"
                        : "No text changes"}
                  </span>
                ) : (
                  r.changes.map((c, i) => (
                    <div className="change-row" key={i}>
                      <span className="change-path">{prettyPath(c.path)}</span>
                      <span className="change-before">{shown(c.before)}</span>
                      <span className="change-arrow" aria-hidden="true">
                        →
                      </span>
                      <span className="change-after">{shown(c.after)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
